/**
 * Booking status rules.
 *
 * A booking's status reaches us as `StatusUtils.title` — a free-text string
 * rather than an enum, so everything that has to interpret one goes through
 * here, and there is a single place to correct if the backend's wording drifts.
 *
 * **The titles and their ids *are* obtainable**, contrary to what this comment
 * used to say: `GET /get-common-utils` returns a `booking_statuses` array
 * alongside `departments_with_roles`, and `axiosSubscriptionInstance` shares a base
 * URL with `axiosSubscriptionInstance`, so the `corporateUtils` payload already
 * cached in sessionStorage comes from the very service that owns bookings. That
 * matters because `POST /modify-booking` wants a `booking_status_id`, not a
 * title — `buildStatusIndex` below is what turns the one into the other, and
 * writing a status was believed impossible for as long as this file claimed
 * otherwise.
 *
 * Reads still key off the title, because that is what the row carries and what
 * the backend's own comparisons use: `NOT ILIKE 'cancelled'` for "still holds
 * the slot" (mirrored by `isActiveBooking`), and — until it was dropped —
 * `ILIKE 'confirmed'` for occupancy.
 *
 * No React, no API — unit-testable in isolation.
 */

/**
 * Fold a status title down to a comparable token: lowercase, alphanumerics only.
 *
 * Matching on the normalised form rather than the raw string is deliberate.
 * Since no endpoint hands us the canonical titles, the filter chips have to
 * recognise whatever the rows happen to carry — `No-show`, `No Show` and
 * `NO_SHOW` all have to land on the same chip.
 *
 * @param {string|null|undefined} title
 * @returns {string} e.g. "noshow". Empty string when there is no status.
 */
export function normaliseStatus(title) {
  if (title == null) return "";
  return String(title).toLowerCase().replace(/[^a-z0-9]/g, "");
}

/**
 * Whether a booking still occupies its slot.
 *
 * Mirrors the backend's own occupancy rule (`su.title NOT ILIKE 'cancelled'` in
 * validate_booking_slot_available and validate_user_booking_conflicts): a
 * booking with no status at all still counts as holding the slot, only an
 * explicit "cancelled" releases it.
 *
 * @param {{ booking_status?: string }} record
 * @returns {boolean}
 */
export function isActiveBooking(record) {
  return normaliseStatus(record?.booking_status) !== "cancelled";
}

/**
 * The five seeded statuses, as `normaliseStatus` tokens.
 *
 * Tokens rather than titles because the title's exact spelling is the backend's
 * to choose — `No Show` is what the seed inserts, but `No-show` would normalise
 * the same and every comparison here goes through the normalised form. Nothing
 * outside this module should spell `"noshow"` inline.
 *
 * These names are load-bearing on the server side: `validate_booking_authority`
 * and `validate_status_timing` compare against six hard-coded lowercase title
 * sets, so renaming a status changes *who may write what, and when*. A rename
 * needs both sides changed together.
 */
export const STATUS = {
  CONFIRMED: "confirmed",
  RESCHEDULED: "rescheduled",
  CANCELLED: "cancelled",
  NO_SHOW: "noshow",
  COMPLETED: "completed",
};

/**
 * Index `booking_statuses` by normalised title, so an id can be found for one.
 *
 * Keyed on the normalised form for the same reason the filter chips are: the
 * canonical spelling is not ours to assume, and `No-show` / `No Show` /
 * `NO_SHOW` all have to resolve to the same row. Rows missing an id or a title
 * are skipped rather than stored half-built — a `booking_status_id` of
 * `undefined` reaches the API as an omitted status, which writes a *plain
 * booking* rather than the status somebody picked.
 *
 * @param {Array<{ id?: string, title?: string }>|null|undefined} bookingStatuses
 *   The `booking_statuses` array from `GET /get-common-utils`.
 * @returns {Map<string, { id: string, title: string }>} Empty when there is
 *   nothing usable, which callers should treat as "not configured" rather than
 *   as "no statuses exist".
 */
export function buildStatusIndex(bookingStatuses) {
  const index = new Map();
  (Array.isArray(bookingStatuses) ? bookingStatuses : []).forEach((row) => {
    const token = normaliseStatus(row?.title);
    if (!token || row?.id == null) return;
    // First wins: `StatusUtils.title` carries no unique constraint, so a
    // duplicate row is possible, and the earlier one is the seeded one.
    if (!index.has(token)) {
      index.set(token, { id: String(row.id), title: String(row.title) });
    }
  });
  return index;
}

/**
 * The `StatusUtils.id` for a token, or null when the index doesn't have it.
 *
 * Null is a meaningful answer and must not be papered over: it means the status
 * exists in our vocabulary but not in the payload we were given, and a write
 * cannot be built for it.
 *
 * @param {Map<string, { id: string }>|null|undefined} index
 * @param {string} token - One of `STATUS`.
 * @returns {string|null}
 */
export function statusIdFor(index, token) {
  return index?.get?.(token)?.id ?? null;
}

/**
 * The backend's own label for a token, falling back to the token itself.
 *
 * Used for anything a person reads, so the UI says whatever the seed says
 * rather than a second, drifting copy of it.
 *
 * @param {Map<string, { title: string }>|null|undefined} index
 * @param {string} token
 * @returns {string}
 */
export function statusTitleFor(index, token) {
  return index?.get?.(token)?.title ?? token;
}

/**
 * Statuses the API refuses until the call has actually ended.
 *
 * Mirrors `_POST_CALL_STATUSES` in `validate_status_timing`, which is a **400,
 * not a 403** — the caller is not being told they lack standing, they are being
 * told the call has not happened. The API exempts executives; the UI does not —
 * nobody may mark a call before it ends.
 *
 * `'rescheduled'` has the opposite rule server-side (`_PRE_CALL_STATUSES`) and
 * is deliberately absent here: nothing on this screen may set it, so mirroring
 * that half would be unreachable code. `'cancelled'` is in neither set — an
 * executive may cancel before or after the call.
 */
const POST_CALL_STATUSES = new Set([STATUS.NO_SHOW, STATUS.COMPLETED]);

/**
 * Whether setting this status requires the call to be over already.
 *
 * @param {string} token - One of `STATUS`.
 * @returns {boolean}
 */
export function requiresEndedCall(token) {
  return POST_CALL_STATUSES.has(token);
}

/**
 * Statuses that must carry a note. A call that didn't happen is the outcome
 * somebody will later ask "why?" about, so the answer is captured with it.
 * Enforced in the Set Status modal.
 */
const NOTE_REQUIRED_STATUSES = new Set([STATUS.NO_SHOW, STATUS.CANCELLED]);

/**
 * Whether setting this status requires a note.
 *
 * @param {string} token - One of `STATUS`.
 * @returns {boolean}
 */
export function requiresNote(token) {
  return NOTE_REQUIRED_STATUSES.has(token);
}

/**
 * Whether a booking's call has finished, as of `now`.
 *
 * Reads `endsAt` before the raw `end_time`: both `bucketBookingsByLocalDate`
 * and `clientTrailForEmployee` spread the raw record and add
 * `startsAt`/`endsAt` as Dates, so a row in hand carries both spellings and the
 * parsed one is the cheaper read.
 *
 * **An unreadable end time answers `true`**, matching the server: "a booking
 * with no `end_time` is not judged", so missing data must not become a refusal.
 * Offering the option and letting the API decide is the behaviour that agrees
 * with it.
 *
 * @param {{ endsAt?: Date, end_time?: string }|null|undefined} booking
 * @param {Date} [now]
 * @returns {boolean}
 */
export function callHasEnded(booking, now = new Date()) {
  const raw = booking?.endsAt ?? booking?.end_time;
  if (!raw) return true;
  const end = raw instanceof Date ? raw : new Date(raw);
  const ms = end.getTime();
  if (Number.isNaN(ms)) return true;
  // `<=`, not `<`: the server's own comparison is `end_time <= NOW()`, so the
  // instant a call ends it is eligible.
  return ms <= now.getTime();
}

/** Normalised title → StatusBadge tone. */
const STATUS_TONES = {
  confirmed: "success",
  completed: "success",
  cancelled: "danger",
  noshow: "brand",
  rescheduled: "info",
};

/**
 * StatusBadge tone for a status title.
 *
 * Falls back to `neutral` rather than throwing or hiding the badge: an
 * unrecognised title is still worth showing verbatim, since it is the only
 * signal we would have that the backend's vocabulary has moved on.
 *
 * @param {string|null|undefined} title
 * @returns {'success'|'danger'|'brand'|'neutral'}
 */
export function statusTone(title) {
  return STATUS_TONES[normaliseStatus(title)] || "neutral";
}
